import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getWeddings, deleteWedding, resyncAllPublicWeddings } from '../../firebase/firestore';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';

const WeddingsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData, isAdmin } = useAuth();
  
  const [weddings, setWeddings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [weddingToDelete, setWeddingToDelete] = useState(null);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchWeddings = async () => {
      try {
        if (!userData) return;
        
        const weddingsData = await getWeddings(userData.uid, isAdmin);
        setWeddings(weddingsData);
      } catch (err) {
        console.error('Error fetching weddings:', err);
        setError(t('common.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchWeddings();
  }, [userData, isAdmin, t]);

  const handleCreateWedding = () => {
    navigate('/weddings/create');
  };

  const handleEditWedding = (weddingId) => {
    navigate(`/weddings/edit/${weddingId}`);
  };

  const handleViewWedding = (weddingId) => {
    navigate(`/weddings/${weddingId}`);
  };

  const handleOpenDeleteDialog = (wedding) => {
    setWeddingToDelete(wedding);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setWeddingToDelete(null);
  };

  const handleDeleteWedding = async () => {
    if (!weddingToDelete) return;
    
    try {
      await deleteWedding(weddingToDelete.id, userData.uid);
      setWeddings(weddings.filter(w => w.id !== weddingToDelete.id));
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting wedding:', err);
      setError(t('common.error'));
    }
  };

  const handleResyncPublicWeddings = async () => {
    try {
      await resyncAllPublicWeddings();
      setSuccess(t('weddings.resyncSuccess'));
    } catch (err) {
      console.error('Error resyncing weddings:', err);
      setError(t('common.error'));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          {t('weddings.title')}
        </Typography>
        <Box>
          {isAdmin && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleResyncPublicWeddings}
              sx={{ mr: 2 }}
            >
              {t('weddings.resyncPublic')}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateWedding}
          >
            {t('weddings.create')}
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : weddings.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('dashboard.noWeddings')}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('weddings.name')}</TableCell>
                <TableCell>{t('weddings.location')}</TableCell>
                <TableCell>{t('weddings.date')}</TableCell>
                <TableCell>{t('weddings.tables')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {weddings.map((wedding) => (
                <TableRow key={wedding.id}>
                  <TableCell>{wedding.name}</TableCell>
                  <TableCell>{wedding.location}</TableCell>
                  <TableCell>
                    {wedding.date ? new Date(wedding.date.toDate()).toLocaleDateString() : ''}
                  </TableCell>
                  <TableCell>{wedding.tables ? wedding.tables.length : 0}</TableCell>
                  <TableCell align="right">
                    <IconButton 
                      color="primary" 
                      onClick={() => handleViewWedding(wedding.id)}
                      title={t('common.view')}
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton 
                      color="secondary" 
                      onClick={() => handleEditWedding(wedding.id)}
                      title={t('common.edit')}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      color="error" 
                      onClick={() => handleOpenDeleteDialog(wedding)}
                      title={t('common.delete')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>{t('common.confirmAction')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('weddings.confirmDelete')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteWedding} color="error" autoFocus>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WeddingsList;
